import { Fragment, useCallback, useEffect, useState } from 'react';
import { api, fmt, fmtDate } from '../api.ts';
import BoardChooser, { type ChooserBoard } from './BoardChooser.tsx';
import Toast from './Toast.tsx';
import UndoDialog from './UndoDialog.tsx';

/*
 * Board to order (060): the page to keep open.
 *
 *   Board not chosen yet
 *              lines that need board whose card has none yet, dated by when
 *              the board would have to be ordered; choosing it here puts it
 *              on the card
 *   Order now  each waiting job's board: what it needs, what is held or on
 *              order for it, what it still needs, and the free stock beside
 *              it -- the planner orders it or uses stock for that job
 *   Coming     what has been ordered and not all arrived, late first
 *
 * Refreshes itself, so a planner can leave it open beside Pre-Press.
 */

interface ToOrderJob {
  jobId: number; jobNumber: string; poNumber: string | null; customer: string;
  deliveryDate: string | null; staged: boolean;
  needs: number; held: number; onOrder: number; short: number; onFloor: boolean;
  orderBy: string | null; urgency: 'LATE' | 'NOW' | 'SOON' | 'LATER' | 'NO_DATE';
}
interface ToOrder {
  itemId: number; name: string; sheets: number; free: number; forStock: number; orderBy: string | null;
  urgency: 'LATE' | 'NOW' | 'SOON' | 'LATER' | 'NO_DATE'; supplierId: number | null; jobs: ToOrderJob[];
}
interface Unchosen {
  jobId: number; jobNumber: string; poNumber: string | null; customer: string;
  box: string; boxSize: string | null; ups: number | null; productionQuantity: number;
  deliveryDate: string | null; staged: boolean; suggestedSheets: number | null;
  orderBy: string | null; urgency: ToOrder['urgency'];
}
interface Coming {
  order_id: number; board_order: string; item_id: number; item_name: string; supplier_name: string;
  sheets: string; received: string; to_come: string; expected_date: string; late: boolean;
  jobs: string[]; letGo: { jobNumber: string; sheets: number; cancelled: boolean }[];
}
interface Supplier { id: number; name: string; lead_days: number }
interface BoardItem { item_id: number; name: string; free: string; active: boolean; below_min: boolean }
/** On the floor without its board, or owed board it lent (066): settled here. */
interface Waiting {
  kind: 'PENDING' | 'OWED'; jobId: number; jobNumber: string; itemId: number; itemName: string;
  sheets: number; free: number; loanId?: number; borrowerJobNumber?: string;
}
type PageData = {
  today: string; lastSupplier: Record<string, number>; toOrder: ToOrder[]; coming: Coming[];
  waiting: Waiting[]; unchosen: Unchosen[];
};

const REFRESH_MS = 60_000;

const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/** "OP12-1, OP8-1" -- the first few jobs, then a count. */
function jobList(names: string[]) {
  return `${names.slice(0, 3).join(', ')}${names.length > 3 ? ` +${names.length - 3}` : ''}`;
}

function forJobs(names: string[]) {
  return names.length === 0 ? 'for stock' : `for ${jobList(names)}`;
}

/** One set of column widths for every table on the page, so they line up. */
function Cols({ order = false }: { order?: boolean }) {
  return (
    <colgroup>
      <col style={{ width: 110 }} /><col />{order && <col style={{ width: 90 }} />}<col style={{ width: '22%' }} />
      <col style={{ width: 110 }} /><col style={{ width: 150 }} /><col style={{ width: 190 }} />
    </colgroup>
  );
}

function OrderBy({ t }: { t: Pick<ToOrder, 'urgency' | 'orderBy'> }) {
  switch (t.urgency) {
    case 'LATE':
      return <span className="tag late" title="The date to order by has passed">Late · {fmtDate(t.orderBy)}</span>;
    case 'NOW': return <span className="tag late">Order today</span>;
    case 'SOON': return <span className="tag waiting">By {fmtDate(t.orderBy)}</span>;
    case 'LATER': return <span className="dim" style={{ fontSize: 13 }}>By {fmtDate(t.orderBy)}</span>;
    default:
      return <span className="dim" style={{ fontSize: 13 }} title="None of these jobs has a delivery date">
        No delivery date
      </span>;
  }
}

export default function BoardToOrder({ canManage, setError, setNotice, onChanged }: {
  canManage: boolean;
  setError: (s: string) => void;
  setNotice: (s: string) => void;
  onChanged: () => void;
}) {
  const [data, setData] = useState<PageData | null>(null);
  const [ordering, setOrdering] = useState<{ t: ToOrder; jobId?: number } | null>(null);
  // "+ Order board": any board, for stock -- no job has to be asking for it.
  const [choosing, setChoosing] = useState(false);
  const [arriving, setArriving] = useState<Coming | null>(null);
  const [choosingFor, setChoosingFor] = useState<Unchosen | null>(null);
  const [using, setUsing] = useState<{ t: ToOrder; j: ToOrderJob } | null>(null);
  const [held, setHeld] = useState<{ text: string; movementId: number; key: number } | null>(null);
  const [undoUrl, setUndoUrl] = useState<string | null>(null);

  const load = useCallback(async (quiet = false) => {
    try {
      setData(await api.get('/api/stock/board-to-order'));
    } catch (err) {
      // A background refresh that fails says nothing; the next one will try again.
      if (!quiet) setError(err instanceof Error ? err.message : 'Could not load board to order');
    }
  }, [setError]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  /*
   * A job on the floor without its board gets it first -- all that is still
   * to come, once that much is free. Board lent to it is given back from free
   * stock, as much as there is.
   */
  async function settle(w: Waiting) {
    setError('');
    try {
      if (w.kind === 'PENDING') {
        await api.post(`/api/jobs/${w.jobId}/boards/${w.itemId}/settle`, {});
        setNotice(`${fmt(String(w.sheets))} sheets of ${w.itemName} issued to ${w.jobNumber}`);
      } else {
        const sheets = Math.min(w.sheets, w.free);
        await api.post(`/api/board-loans/${w.loanId}/give-back`, { sheets });
        setNotice(`${fmt(String(sheets))} sheets given back to ${w.jobNumber}`);
      }
      await load();
      onChanged();
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not settle that'); }
  }

  async function close(c: Coming) {
    const back = c.jobs.length > 0;
    const reason = window.prompt(
      `Close ${c.board_order} for ${c.item_name} from ${c.supplier_name}? The ${fmt(c.to_come)} sheets still to come `
      + (back ? `will not arrive; what ${c.jobs.join(', ')} still need goes back to Order now.`
        : 'will not arrive, and no job is waiting for them.')
      + ' Reason (optional):', '');
    if (reason === null) return;
    setError('');
    try {
      await api.post(`/api/stock/board-orders/${c.order_id}/close`, reason.trim() ? { reason: reason.trim() } : {});
      setNotice(back ? `Order closed — what ${c.jobs.join(', ')} still need is back in Order now`
        : `Order closed — ${fmt(c.to_come)} sheets of ${c.item_name} will not come`);
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not close the order'); }
  }

  if (!data) return <div className="empty">Loading…</div>;
  const needs = data.toOrder.flatMap((t) => t.jobs.map((j) => ({ t, j })))
    .sort((a, b) => (a.j.orderBy ?? '9999').localeCompare(b.j.orderBy ?? '9999')
      || (a.j.deliveryDate ?? '9999').localeCompare(b.j.deliveryDate ?? '9999') || b.j.short - a.j.short);
  const late = data.coming.filter((c) => c.late).length;

  return (
    <div style={{ padding: '4px 16px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '10px 0 14px' }}>
        <span className="count" style={{ color: 'var(--muted)', fontSize: 13 }}>
          {data.waiting.length ? `${data.waiting.length} waiting · ` : ''}
          {data.unchosen.length ? `${data.unchosen.length} board not chosen · ` : ''}
          {needs.length} to order · {data.coming.length} coming{late ? ` · ${late} late` : ''}
        </span>
        <span style={{ marginLeft: 'auto' }} />
        {canManage && <button className="btn" onClick={() => setChoosing(true)}>+ Order board</button>}
      </div>

      {data.waiting.length > 0 && (
        <>
          <div className="order-section">Waiting for board · {data.waiting.length}</div>
          <div className="order-list">
            <table className="order-table">
              <Cols />
              <thead>
                <tr><th>Job</th><th>Board</th><th>Why</th><th className="num">Sheets</th><th>Free now</th><th /></tr>
              </thead>
              <tbody>
                {data.waiting.map((w) => {
                  const ready = w.kind === 'PENDING' ? w.free >= w.sheets : w.free > 0;
                  return (
                    <tr key={`${w.kind}-${w.loanId ?? w.jobId}-${w.itemId}`}>
                      <td className="strong">{w.jobNumber}</td>
                      <td>{w.itemName}</td>
                      <td>{w.kind === 'PENDING' ? 'On the floor without it' : `Lent to ${w.borrowerJobNumber}`}</td>
                      <td className="num strong">{fmt(String(w.sheets))}</td>
                      <td className="dim">{fmt(String(w.free))} free</td>
                      <td className="act">
                        {canManage && (ready ? (
                          <button className="btn primary" onClick={() => void settle(w)}>
                            {w.kind === 'PENDING' ? `Issue ${fmt(String(w.sheets))}`
                              : `Give back ${fmt(String(Math.min(w.sheets, w.free)))}`}
                          </button>
                        ) : (
                          <span className="dim" title="Stock in or receive board first">Waiting for board</span>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {data.unchosen.length > 0 && (
        <>
          <div className="order-section">Board not chosen yet · {data.unchosen.length}</div>
          <div className="order-list">
            <table className="order-table">
              <Cols />
              <thead>
                <tr><th>Job</th><th>Box</th><th>Customer</th><th className="num">Sheets</th><th>Order by</th><th /></tr>
              </thead>
              <tbody>
                {data.unchosen.map((u) => (
                  <tr key={u.jobId}>
                    <td className="strong">{u.jobNumber}</td>
                    <td>
                      <div>{u.box}</div>
                      <div className="dim sub">
                        {[u.boxSize, `${fmt(String(u.productionQuantity))} to run`, u.ups ? `${u.ups} ups` : null]
                          .filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td>{u.customer}</td>
                    <td className="num strong">
                      {u.suggestedSheets ? fmt(String(u.suggestedSheets))
                        : <span className="dim" title="Enter the ups on the order line to work this out">—</span>}
                    </td>
                    <td><OrderBy t={u} /></td>
                    <td className="act">
                      {canManage && <button className="btn" onClick={() => setChoosingFor(u)}>Choose board</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="order-section">Order now{needs.length ? ` · ${needs.length}` : ''}</div>
      {needs.length === 0 ? (
        <div className="order-list"><div className="order-row dim">All board is covered — nothing to order.</div></div>
      ) : (
        <div className="order-list need-list">
          <table className="order-table">
            <colgroup>
              <col style={{ width: 112 }} /><col /><col style={{ width: 76 }} /><col style={{ width: 76 }} />
              <col style={{ width: 100 }} /><col style={{ width: 96 }} /><col style={{ width: 150 }} />
              <col style={{ width: 190 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Job</th><th>Board</th><th className="num">Needs</th><th className="num">Held</th>
                <th className="num">Still needed</th><th className="num">Free stock</th><th>Order by</th><th />
              </tr>
            </thead>
            <tbody>
              {needs.map(({ t, j }) => (
                <tr key={`${t.itemId}-${j.jobId}`}>
                  <td>
                    <div className="strong">{j.jobNumber}</div>
                    <div className="dim sub">{j.customer}</div>
                    {j.onFloor && <span className="tag waiting" style={{ marginTop: 4 }}>On the floor</span>}
                  </td>
                  <td>
                    <div>{t.name}</div>
                    {t.jobs.length > 1 && (
                      <div className="dim sub">
                        +{t.jobs.length - 1} more {t.jobs.length > 2 ? 'jobs' : 'job'} on this board
                      </div>
                    )}
                  </td>
                  <td className="num">{fmt(String(j.needs))}</td>
                  <td className="num">
                    <div className={j.held ? undefined : 'dim'}>{j.held ? fmt(String(j.held)) : '—'}</div>
                    {j.onOrder > 0 && <div className="dim sub">+{fmt(String(j.onOrder))} on order</div>}
                  </td>
                  <td className="num strong">{fmt(String(j.short))}</td>
                  <td className="num">
                    <div className={t.free ? undefined : 'dim'}>{fmt(String(t.free))}</div>
                    {t.forStock > 0 && <div className="dim sub">+{fmt(String(t.forStock))} on order</div>}
                  </td>
                  <td><OrderBy t={j} /></td>
                  <td className="act">
                    {canManage && (
                      <>
                        {!j.onFloor && t.free > 0 && (
                          <button className="btn" onClick={() => setUsing({ t, j })}>Use stock</button>
                        )}
                        <button className="btn" style={{ marginLeft: 8 }}
                                onClick={() => setOrdering({ t, jobId: j.jobId })}>Order</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="order-section">Coming{data.coming.length ? ` · ${data.coming.length}` : ''}</div>
      {data.coming.length === 0 ? (
        <div className="order-list"><div className="order-row dim">Nothing on order.</div></div>
      ) : (
        <div className="order-list">
          <table className="order-table">
            <Cols order />
            <thead>
              <tr><th>Jobs</th><th>Board</th><th>Order</th><th>Supplier</th><th className="num">Sheets</th><th>Expected</th><th /></tr>
            </thead>
            <tbody>
              {data.coming.map((c) => {
                const part = Number(c.received) > 0;
                return (
                  <tr key={c.order_id}>
                    <td className="strong" title={c.jobs.join(', ')}>
                      {c.jobs.slice(0, 3).map((j) => <div key={j}>{j}</div>)}
                      {c.jobs.length > 3 && <div className="dim sub">+{c.jobs.length - 3} more</div>}
                      {!c.jobs.length && !c.letGo.length && <span className="dim">For stock</span>}
                      {c.letGo.map((g) => <div key={g.jobNumber} className="dim sub"><s>{g.jobNumber}</s></div>)}
                    </td>
                    <td>
                      <div>{c.item_name}</div>
                      {c.letGo.map((g) => (
                        <div key={g.jobNumber} className="dim sub">
                          {g.jobNumber} {g.cancelled ? 'cancelled' : 'changed board'}
                          {g.sheets > 0 ? ` · ${fmt(String(g.sheets))} sheets now for stock` : ''}
                        </div>
                      ))}
                      {c.letGo.length > 0 && c.jobs.length === 0 && (
                        <span className="tag waiting" style={{ marginTop: 4 }}
                              title="Close it if the supplier can stop it; otherwise it arrives as free stock">
                          No job needs this now
                        </span>
                      )}
                    </td>
                    <td className="mono">{c.board_order}</td>
                    <td>{c.supplier_name}</td>
                    <td className="num">
                      <div className="strong">{fmt(c.to_come)}</div>
                      {part && <div className="dim sub">of {fmt(c.sheets)}</div>}
                    </td>
                    <td>
                      {c.late
                        ? <span className="tag late">Late · {fmtDate(c.expected_date)}</span>
                        : <span className="dim">{fmtDate(c.expected_date)}</span>}
                    </td>
                    <td className="act">
                      {canManage && (
                        <>
                          <button className="btn" style={{ minWidth: 104 }} onClick={() => setArriving(c)}>
                            {part ? 'Arrived rest' : 'Arrived'}
                          </button>
                          <button className="linkish" style={{ marginLeft: 10 }} title="The supplier will not send the rest"
                                  onClick={() => void close(c)}>Close</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {choosingFor && (
        <ChooseForJob u={choosingFor} onClose={() => setChoosingFor(null)}
                      onDone={async (itemId, andOrder) => {
                        const u = choosingFor;
                        setChoosingFor(null);
                        onChanged();
                        const next = await api.get<PageData>('/api/stock/board-to-order').catch(() => null);
                        if (next) setData(next);
                        const row = next?.toOrder.find((t) => t.itemId === itemId);
                        const short = row?.jobs.some((j) => j.jobId === u.jobId) ?? false;
                        if (andOrder && row && short) { setOrdering({ t: row, jobId: u.jobId }); return; }
                        setNotice(short
                          ? `Board chosen for ${u.jobNumber} — it is now in Order now`
                          : `Board chosen for ${u.jobNumber} — stock covers it, nothing to order`);
                      }} />
      )}
      {choosing && (
        <ChooseBoard onClose={() => setChoosing(false)}
                     onPick={(b) => {
                       setChoosing(false);
                       // A board jobs are short of brings its jobs; any other is for stock.
                       setOrdering({ t: data.toOrder.find((t) => t.itemId === b.item_id) ?? {
                         itemId: b.item_id, name: b.name, sheets: 0, free: 0, forStock: 0, orderBy: null, urgency: 'NO_DATE',
                         supplierId: data.lastSupplier[String(b.item_id)] ?? null, jobs: [],
                       } });
                     }} />
      )}
      {ordering && (
        <OrderDialog t={ordering.t} forJob={ordering.jobId} today={data.today} onClose={() => setOrdering(null)}
                     onDone={(msg) => { setOrdering(null); setNotice(msg); void load(); onChanged(); }} />
      )}
      {arriving && (
        <ArrivedDialog c={arriving} today={data.today} onClose={() => setArriving(null)}
                       onDone={(msg) => { setArriving(null); setNotice(msg); void load(); onChanged(); }} />
      )}
      {using && (
        <UseStockDialog t={using.t} j={using.j} onClose={() => setUsing(null)}
                        onDone={(text, movementId) => {
                          setUsing(null);
                          setHeld({ text, movementId, key: Date.now() });
                          void load(); onChanged();
                        }} />
      )}
      {held && (
        <Toast key={held.key} onClose={() => setHeld(null)}>
          <span>{held.text}</span>
          <button className="toast-act" onClick={() => {
            setUndoUrl(`/api/stock/board-movements/${held.movementId}/undo`);
            setHeld(null);
          }}>Undo</button>
        </Toast>
      )}
      {undoUrl && (
        <UndoDialog previewUrl={undoUrl} onClose={() => setUndoUrl(null)}
                    onDone={(msg) => { setNotice(msg); void load(); onChanged(); }} />
      )}
    </div>
  );
}

/**
 * Order one board from one supplier. Everything is filled in: the supplier it
 * was last bought from, every job that needs it (untick any not to buy for
 * yet), and the date it should come by that supplier's usual days.
 */
/**
 * The order window for one board, from anywhere: the jobs short of it come
 * ticked, as on To order; a board no job is short of is ordered for stock.
 */
export function OrderBoard({ itemId, name, onClose, onDone }: {
  itemId: number; name: string; onClose: () => void; onDone: (msg: string) => void;
}) {
  const [data, setData] = useState<PageData | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api.get<PageData>('/api/stock/board-to-order').then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load To order'));
  }, []);
  if (error) {
    return (
      <div className="backdrop" onClick={onClose}>
        <div className="modal" style={{ width: 420 }} onClick={(e) => e.stopPropagation()}>
          <div className="modal-body"><div className="error" style={{ marginBottom: 0 }}>{error}</div></div>
          <div className="modal-foot"><button className="btn" onClick={onClose}>Close</button></div>
        </div>
      </div>
    );
  }
  if (!data) return null;
  const t = data.toOrder.find((x) => x.itemId === itemId) ?? {
    itemId, name, sheets: 0, free: 0, forStock: 0, orderBy: null, urgency: 'NO_DATE' as const,
    supplierId: data.lastSupplier[String(itemId)] ?? null, jobs: [],
  };
  return <OrderDialog t={t} today={data.today} onClose={onClose} onDone={onDone} />;
}

function OrderDialog({ t, forJob, today, onClose, onDone }: {
  t: ToOrder; forJob?: number; today: string; onClose: () => void; onDone: (msg: string) => void;
}) {
  const first = t.jobs.find((j) => j.jobId === forJob);
  const jobs = first ? [first, ...t.jobs.filter((j) => j !== first)] : t.jobs;
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierId, setSupplierId] = useState(t.supplierId ? String(t.supplierId) : '');
  const [picked, setPicked] = useState<Record<number, string>>(
    Object.fromEntries((first ? [first] : t.jobs).map((j) => [j.jobId, String(j.short)])));
  const [extra, setExtra] = useState('');
  const [expected, setExpected] = useState('');
  const [dateTouched, setDateTouched] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<{ suppliers: Supplier[] }>('/api/reference/suppliers').then((r) => {
      setSuppliers(r.suppliers);
      setSupplierId((s) => s || String(r.suppliers[0]?.id ?? ''));
    }).catch(() => setError('Could not load the suppliers'));
  }, []);

  // The expected date follows the supplier until someone types their own.
  const supplier = suppliers.find((s) => String(s.id) === supplierId);
  useEffect(() => {
    if (!dateTouched && supplier) setExpected(addDays(today, supplier.lead_days));
  }, [supplier, today, dateTouched]);

  const jobLines = jobs.filter((j) => picked[j.jobId] !== undefined)
    .map((j) => ({ jobId: j.jobId, sheets: Number(picked[j.jobId]) }));
  const extraN = extra.trim() === '' ? 0 : Number(extra);
  const total = jobLines.reduce((n, j) => n + (Number.isFinite(j.sheets) ? j.sheets : 0), 0) + (extraN || 0);
  const problem = !supplierId ? 'Choose the supplier'
    : jobLines.some((j) => !Number.isInteger(j.sheets) || j.sheets <= 0) ? 'Sheets for each ticked job, in whole sheets'
    : !Number.isInteger(extraN) || extraN < 0 ? 'Extra for stock is whole sheets'
    : total <= 0 ? (t.jobs.length ? 'Tick a job or add sheets for stock' : 'How many sheets?')
    : !expected ? 'When is it expected?'
    : expected < today ? 'The expected date has already gone' : '';

  async function submit() {
    if (problem) return;
    setBusy(true); setError('');
    try {
      const r = await api.post<{ sheets: number; supplier: string }>('/api/stock/board-orders', {
        itemId: t.itemId, supplierId: Number(supplierId), expectedDate: expected,
        jobs: jobLines, extraSheets: extraN,
      });
      onDone(`Ordered ${fmt(String(r.sheets))} sheets of ${t.name} from ${r.supplier} · due ${fmtDate(expected)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not place the order');
    } finally { setBusy(false); }
  }

  return (
    <div className="backdrop" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ width: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Order {t.name}</h3>
          <p>{first ? `${first.jobNumber} · ${first.customer} · still needs ${fmt(String(first.short))} sheets`
            : t.jobs.length ? `${fmt(String(t.sheets))} sheets still needed` : 'For stock'}</p>
        </div>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          <div className="row2">
            <div className="field">
              <label htmlFor="bo-supplier" className="req">Supplier</label>
              <select id="bo-supplier" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="bo-expected" className="req">Expected</label>
              <input id="bo-expected" type="date" min={today} value={expected}
                     onChange={(e) => { setDateTouched(true); setExpected(e.target.value); }} />
              {supplier && <div className="hint">{supplier.name} usually takes {supplier.lead_days} days</div>}
            </div>
          </div>

          {t.jobs.length === 0 ? (
            <div className="field">
              <label htmlFor="bo-sheets" className="req">Sheets</label>
              <input id="bo-sheets" type="number" min="1" step="1" autoFocus value={extra}
                     onChange={(e) => setExtra(e.target.value)} />
              <div className="hint">No waiting job needs this board — it goes to free stock when it arrives.</div>
            </div>
          ) : (
          <div className="field">
            <label>For</label>
            <div className="order-list" style={{ marginBottom: 0 }}>
              {jobs.map((j, i) => {
                const on = picked[j.jobId] !== undefined;
                return (
                  <Fragment key={j.jobId}>
                    {first && i === 1 && (
                      <div className="order-row dim" style={{ padding: '6px 12px', fontSize: 12 }}>
                        Also need this board — tick to order together
                      </div>
                    )}
                    <label className="order-row" style={{ cursor: 'pointer', padding: '8px 12px' }}>
                      <input type="checkbox" checked={on}
                             onChange={() => setPicked((p) => {
                               const n = { ...p };
                               if (on) delete n[j.jobId]; else n[j.jobId] = String(j.short);
                               return n;
                             })} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <strong>{j.jobNumber}</strong>
                        <span className="dim"> · {j.customer}{j.deliveryDate ? ` · due ${fmtDate(j.deliveryDate)}` : ''}</span>
                      </span>
                      <input type="number" min="1" step="1" aria-label={`Sheets for ${j.jobNumber}`}
                             disabled={!on} style={{ width: 100, textAlign: 'right' }}
                             value={on ? picked[j.jobId] : String(j.short)}
                             onClick={(e) => e.preventDefault()}
                             onChange={(e) => setPicked((p) => ({ ...p, [j.jobId]: e.target.value }))} />
                    </label>
                  </Fragment>
                );
              })}
              <div className="order-row" style={{ padding: '8px 12px' }}>
                <span style={{ flex: 1 }} className="dim">Extra for stock</span>
                <input type="number" min="0" step="1" aria-label="Extra sheets for stock" placeholder="0"
                       style={{ width: 100, textAlign: 'right' }} value={extra}
                       onChange={(e) => setExtra(e.target.value)} />
              </div>
              <div className="order-row" style={{ padding: '10px 12px', background: 'var(--surface-2, transparent)' }}>
                <strong style={{ flex: 1 }}>Total</strong>
                <strong style={{ width: 100, textAlign: 'right', paddingRight: 12 }}>{fmt(String(total))}</strong>
              </div>
            </div>
            <div className="hint">Sheets for a ticked job are held for it when they arrive.</div>
          </div>
          )}
        </div>
        <div className="modal-foot">
          <span className="hint" style={{ marginRight: 'auto' }}>{problem ?? ''}</span>
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={() => void submit()} disabled={busy || !!problem}>
            {busy ? 'Ordering…' : 'Place order'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Set free sheets aside for one job, so no other job can take them. */
function UseStockDialog({ t, j, onClose, onDone }: {
  t: ToOrder; j: ToOrderJob; onClose: () => void; onDone: (text: string, movementId: number) => void;
}) {
  const [sheets, setSheets] = useState(String(Math.min(t.free, j.short)));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const n = Number(sheets);
  const problem = !(Number.isInteger(n) && n > 0) ? 'Sheets, in whole sheets'
    : n > t.free ? `Only ${fmt(String(t.free))} free`
    : n > j.short ? `${j.jobNumber} still needs only ${fmt(String(j.short))}` : '';
  const left = j.short - (problem ? 0 : n);

  async function submit() {
    if (problem) return;
    setBusy(true); setError('');
    try {
      const r = await api.post<{ movementId: number; sheets: number; stillNeeded: number }>(
        `/api/stock/board-to-order/jobs/${j.jobId}/hold`, { itemId: t.itemId, sheets: n });
      onDone(`${fmt(String(r.sheets))} sheets held for ${j.jobNumber}`
        + (r.stillNeeded > 0 ? ` · still needs ${fmt(String(r.stillNeeded))}` : ''), r.movementId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not hold the sheets');
      setBusy(false);
    }
  }

  return (
    <div className="backdrop" onClick={() => !busy && onClose()}>
      <div className="modal" role="dialog" aria-label={`Use stock for ${j.jobNumber}`} style={{ width: 460 }}
           onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Use stock for {j.jobNumber}</h3>
          <p>{j.customer} · {t.name}</p>
        </div>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          <div className="field" style={{ maxWidth: 220 }}>
            <label htmlFor="us-sheets" className="req">Sheets to hold</label>
            <input id="us-sheets" type="number" min="1" step="1" autoFocus value={sheets}
                   onFocus={(e) => e.target.select()}
                   onChange={(e) => setSheets(e.target.value)} />
            <div className="hint">{fmt(String(t.free))} free · still needs {fmt(String(j.short))}</div>
          </div>
          {!problem && (
            <ul className="undo-list">
              <li>{fmt(String(n))} sheets held for {j.jobNumber} — no other job can take them</li>
              <li>Free stock goes from {fmt(String(t.free))} to {fmt(String(t.free - n))}</li>
              <li>{left > 0 ? `${j.jobNumber} still needs ${fmt(String(left))}` : `${j.jobNumber} leaves Order now`}</li>
            </ul>
          )}
        </div>
        <div className="modal-foot">
          {problem && <span className="hint" style={{ marginRight: 'auto' }}>{problem}</span>}
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={() => void submit()} disabled={busy || !!problem}>
            {busy ? 'Holding…' : `Hold ${problem ? '' : `${fmt(String(n))} `}sheets`}
          </button>
        </div>
      </div>
    </div>
  );
}

/** A delivery against an order: counted sheets, and the bill if there is one. */
function ArrivedDialog({ c, today, onClose, onDone }: {
  c: Coming; today: string; onClose: () => void; onDone: (msg: string) => void;
}) {
  const [sheets, setSheets] = useState(String(Number(c.to_come)));
  const [billNo, setBillNo] = useState('');
  const [price, setPrice] = useState('');
  const [date, setDate] = useState(today);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const n = Number(sheets);
  const problem = !(Number.isInteger(n) && n > 0) ? 'Sheets received, in whole sheets'
    : price.trim() && !(Number(price) >= 0) ? 'Price is a number'
    : date > today ? 'Not in the future' : '';
  const short = Number(c.to_come) - n;

  async function submit() {
    if (problem) return;
    setBusy(true); setError('');
    try {
      const r = await api.post<{
        toCome: number; toStock: number; forJobs: { jobNumber: string; sheets: number }[];
      }>(
        `/api/stock/board-orders/${c.order_id}/receive`, {
          sheets: n, movementDate: date,
          ...(billNo.trim() ? { billNo: billNo.trim() } : {}),
          ...(price.trim() ? { unitPrice: Number(price) } : {}),
        });
      const held = r.forJobs.length
        ? ` · held for ${r.forJobs.map((f) => `${f.jobNumber} (${fmt(String(f.sheets))})`).join(', ')}` : '';
      // Say where every sheet went: held for a job, or free for anyone.
      const free = r.forJobs.length && r.toStock > 0 ? ` · ${fmt(String(r.toStock))} to free stock` : '';
      onDone(`${fmt(String(n))} sheets of ${c.item_name} in stock${held}${free}`
        + (r.toCome > 0 ? ` · ${fmt(String(r.toCome))} still to come` : ''));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the delivery');
    } finally { setBusy(false); }
  }

  return (
    <div className="backdrop" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ width: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Arrived · {c.board_order}</h3>
          <p>{c.item_name} · {c.supplier_name} · {fmt(c.to_come)} sheets to come · {forJobs(c.jobs)}</p>
        </div>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          <div className="row2">
            <div className="field">
              <label htmlFor="ba-sheets" className="req">Sheets received</label>
              <input id="ba-sheets" type="number" min="1" step="1" autoFocus value={sheets}
                     onChange={(e) => setSheets(e.target.value)} />
              {n > 0 && short > 0 && <div className="hint">{fmt(String(short))} stays on order</div>}
              {n > 0 && short < 0 && <div className="hint">{fmt(String(-short))} more than ordered — goes to stock</div>}
            </div>
            <div className="field">
              <label htmlFor="ba-date">Date</label>
              <input id="ba-date" type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
          <div className="row2">
            <div className="field">
              <label htmlFor="ba-bill">Bill no.</label>
              <input id="ba-bill" type="text" value={billNo} onChange={(e) => setBillNo(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="ba-price">Price per kg (₹)</label>
              <input id="ba-price" type="number" min="0" step="0.01" value={price}
                     onChange={(e) => setPrice(e.target.value)} />
            </div>
          </div>
          <div className="hint">Count only good sheets. Damaged ones are not received.</div>
        </div>
        <div className="modal-foot">
          {problem && <span className="hint" style={{ marginRight: 'auto' }}>{problem}</span>}
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={() => void submit()} disabled={busy || !!problem}>
            {busy ? 'Saving…' : 'Into stock'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The first step of "+ Order board": which board, or a new one. Low boards say so. */
function ChooseBoard({ onClose, onPick }: { onClose: () => void; onPick: (b: BoardItem) => void }) {
  const [boards, setBoards] = useState<BoardItem[]>([]);
  useEffect(() => {
    api.get<{ items: BoardItem[] }>('/api/stock/board-items')
      .then((r) => setBoards(r.items.filter((i) => i.active))).catch(() => {});
  }, []);
  const pick = (v: string) => { const b = boards.find((x) => String(x.item_id) === v); if (b) onPick(b); };
  return (
    <div className="backdrop" onClick={onClose}>
      <div className="modal" style={{ width: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><h3>Order board</h3><p>Any board, for stock or for jobs</p></div>
        <div className="modal-body">
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="bo-board" className="req">Board</label>
            <BoardChooser id="bo-board" value="" autoFocus canAdd onChange={pick}
                          boards={boards.map((b) => ({
                            item_id: b.item_id, name: b.name,
                            hint: `${fmt(b.free)} free${b.below_min ? ' · Low' : ''}`,
                          }))}
                          onCreated={(b) => onPick({ item_id: b.item_id, name: b.name, free: '0', active: true, below_min: false })} />
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Choose the board for a line whose card has none yet. It goes on the card --
 * the same as choosing it there -- and the box remembers it for next time.
 */
function ChooseForJob({ u, onClose, onDone }: {
  u: Unchosen; onClose: () => void; onDone: (itemId: number, andOrder: boolean) => void;
}) {
  const [boards, setBoards] = useState<ChooserBoard[]>([]);
  const [itemId, setItemId] = useState('');
  const [sheets, setSheets] = useState(u.suggestedSheets ? String(u.suggestedSheets) : '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<{ items: BoardItem[] }>('/api/stock/board-items').then((r) => setBoards(
      r.items.filter((i) => i.active).map((i) => ({ item_id: i.item_id, name: i.name, hint: `${fmt(i.free)} free` })),
    )).catch(() => setError('Could not load the boards'));
  }, []);

  const n = Number(sheets);
  const problem = !itemId ? 'Choose the board' : !Number.isInteger(n) || n <= 0 ? 'Sheets, in whole sheets' : '';

  async function save(andOrder: boolean) {
    if (problem) return;
    setBusy(true); setError('');
    try {
      await api.put(`/api/stock/board-to-order/jobs/${u.jobId}/board`, { itemId: Number(itemId), sheets: n });
      onDone(Number(itemId), andOrder);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the board');
      setBusy(false);
    }
  }

  return (
    <div className="backdrop" onClick={() => !busy && onClose()}>
      <div className="modal" role="dialog" aria-label={`Board for ${u.jobNumber}`} style={{ width: 560 }}
           onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Board for {u.jobNumber} · {u.box}</h3>
          <p>{[u.customer, u.boxSize, `${fmt(String(u.productionQuantity))} to run`, u.ups ? `${u.ups} ups` : null]
            .filter(Boolean).join(' · ')}</p>
        </div>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          <div className="field">
            <label htmlFor="cj-board" className="req">Board</label>
            <BoardChooser id="cj-board" value={itemId} onChange={setItemId} autoFocus canAdd boards={boards}
                          onCreated={(b) => setBoards((p) => [...p, { ...b, hint: '0 free' }])} />
          </div>
          <div className="field" style={{ marginBottom: 0, maxWidth: 220 }}>
            <label htmlFor="cj-sheets" className="req">Sheets</label>
            <input id="cj-sheets" type="number" min="1" step="1" value={sheets}
                   onChange={(e) => setSheets(e.target.value)} />
            <div className="hint">
              {u.suggestedSheets
                ? `${fmt(String(u.productionQuantity))} to run ÷ ${u.ups} ups, rounded up`
                : 'Enter the ups on the order line to have this worked out'}
            </div>
          </div>
          <div className="hint" style={{ marginTop: 12 }}>
            It goes on the job card, and the box remembers it for its next order.
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn" disabled={busy || !!problem} onClick={() => void save(false)}>Save board</button>
          <button className="btn primary" disabled={busy || !!problem} onClick={() => void save(true)}>
            Save and order
          </button>
        </div>
      </div>
    </div>
  );
}
